import {
  PROTOCOL_VERSION,
  encodeMessage,
  type InputCommand,
  type ServerMessage,
} from "@neon-fuse/netcode";
import type { GameSnapshot } from "@neon-fuse/game-core";

export interface RoomClientOptions {
  readonly roomId: string;
  readonly playerId?: string;
  readonly displayName?: string;
  readonly baseUrl?: string;
  readonly onMessage?: (message: ServerMessage) => void;
  readonly onStatus?: (status: RoomStatus) => void;
}

export type RoomStatus =
  "idle" | "connecting" | "connected" | "reconnecting" | "closed" | "error";

export class RoomClient {
  private readonly options: RoomClientOptions;
  private socket: WebSocket | undefined;
  private sequence = 0;
  private playerId: string | undefined;
  private reconnectToken: string | undefined;
  private lastServerTick = 0;
  private snapshot: GameSnapshot | undefined;
  private status: RoomStatus = "idle";

  public constructor(options: RoomClientOptions) {
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(options.roomId)) {
      throw new RangeError(
        "roomId must use only letters, numbers, underscores, or hyphens",
      );
    }
    this.options = options;
    this.playerId = options.playerId;
  }

  public connect(): void {
    if (this.socket !== undefined) return;
    this.setStatus(this.status === "closed" ? "reconnecting" : "connecting");
    const base =
      this.options.baseUrl ??
      import.meta.env.VITE_API_BASE_URL ??
      window.location.origin;
    const url = new URL(`/rooms/${this.options.roomId}/ws`, base);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(url);
    this.socket = socket;
    socket.addEventListener("open", () => {
      this.setStatus("connected");
      this.send({
        type: "join",
        protocolVersion: PROTOCOL_VERSION,
        ...(this.playerId === undefined
          ? {}
          : { requestedPlayerId: this.playerId }),
        ...(this.options.displayName === undefined
          ? {}
          : { displayName: this.options.displayName }),
      });
    });
    socket.addEventListener("message", (event) =>
      this.handleMessage(event.data),
    );
    socket.addEventListener("close", () => {
      this.socket = undefined;
      if (this.reconnectToken !== undefined) this.setStatus("reconnecting");
      else this.setStatus("closed");
    });
    socket.addEventListener("error", () => this.setStatus("error"));
  }

  public sendInput(input: InputCommand, clientTick: number): void {
    if (
      this.playerId === undefined ||
      this.socket === undefined ||
      this.socket.readyState !== WebSocket.OPEN
    )
      return;
    this.send({
      type: "input",
      protocolVersion: PROTOCOL_VERSION,
      playerId: this.playerId,
      clientTick,
      sequence: this.sequence,
      input,
    });
    this.sequence += 1;
  }

  public reconnect(): void {
    if (this.reconnectToken === undefined || this.playerId === undefined) {
      this.connect();
      return;
    }
    this.setStatus("reconnecting");
    const base =
      this.options.baseUrl ??
      import.meta.env.VITE_API_BASE_URL ??
      window.location.origin;
    const url = new URL(`/rooms/${this.options.roomId}/ws`, base);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(url);
    this.socket = socket;
    socket.addEventListener("open", () => {
      this.send({
        type: "reconnect",
        protocolVersion: PROTOCOL_VERSION,
        playerId: this.playerId as string,
        reconnectToken: this.reconnectToken as string,
        lastServerTick: this.lastServerTick,
        lastAckSequence: this.sequence - 1,
      });
    });
    socket.addEventListener("message", (event) =>
      this.handleMessage(event.data),
    );
    socket.addEventListener("close", () => {
      this.socket = undefined;
      this.setStatus("closed");
    });
    socket.addEventListener("error", () => this.setStatus("error"));
  }

  public close(): void {
    this.socket?.close(1000, "client closed");
    this.socket = undefined;
    this.setStatus("closed");
  }

  public get currentStatus(): RoomStatus {
    return this.status;
  }

  public get assignedPlayerId(): string | undefined {
    return this.playerId;
  }

  public get latestSnapshot(): GameSnapshot | undefined {
    return this.snapshot;
  }

  private handleMessage(serialized: unknown): void {
    if (typeof serialized !== "string") return;
    let message: ServerMessage;
    try {
      message = JSON.parse(serialized) as ServerMessage;
    } catch {
      this.setStatus("error");
      return;
    }
    if ("serverTick" in message && typeof message.serverTick === "number")
      this.lastServerTick = message.serverTick;
    if ("snapshot" in message && message.snapshot !== undefined)
      this.snapshot = message.snapshot;
    if (message.type === "joined") {
      this.playerId = message.playerId;
      this.reconnectToken = message.reconnectToken;
    }
    this.options.onMessage?.(message);
  }

  private send(message: Parameters<typeof encodeMessage>[0]): void {
    this.socket?.send(encodeMessage(message));
  }

  private setStatus(status: RoomStatus): void {
    this.status = status;
    this.options.onStatus?.(status);
  }
}
